import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';                          // r186
import { useAuth } from '../../context/AuthContext';
import { usePermissoes } from '../../context/PermissoesContext';
import tarefaService from '../../services/tarefa.service';
import BtnIcone from '../../components/BtnIcone';
import SePode from '../../components/SePode';
import SelectOrdemTarefas, { IconeAbaLista, IconeAbaAgenda, IconeBusca } from '../../components/SelectOrdemTarefas';   // r220   // r88
import { normalizarFiltros } from '../../components/TarefasFiltrosBar';      // r171
import TarefaModal from './TarefaModal';
import BrowseHeader from '../../components/BrowseHeader';                    // r111
import PopupNotificacoesTarefas, { useAlertaNotificacoes }                   // r186
  from '../../components/Notificacoes/PopupNotificacoesTarefas';
import { iconeDaRotina } from '../../constants/iconesRotinas';               // r111 (apenas pra futuros usos)

// ════════════════════════════════════════════════════════════════════════════
// Página principal de Tarefas — Fase 1 (Backbone)
// ────────────────────────────────────────────────────────────────────────────
// Lista em cards conforme print do MODULO_TAREFAS.md. Filtros e ordenações
// avançadas são da Fase 3. Aqui só temos o básico:
//   - Busca por título
//   - Filtro: minhas (responsável=eu) | criadas por mim | todas
//   - Filtro: status execução (todos / não finalizadas / finalizadas)
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:     '#003399', azulBg:   '#eff6ff',
  borda:    '#e8eaed', texto:    '#1a1a2e', muted: '#888',
  branco:   '#fff',    cinzaBg:  '#fafafa',
  verde:    '#10b981', verdeBg:  '#f0fff4',
  // r179: verde das horas apontadas e do icone de responsavel (COR.verde segue
  //       valendo pro resto -- ex.: a pilula de prioridade Baixa).
  verdeHoras: '#008700',
  // r178: verde exclusivo do status 'Em Andamento' (nao reaproveitar noutros
  //       elementos -- COR.verde segue valendo pro resto).
  verdeAndamento: '#009900',
  vermelho: '#dc2626', vermBg:   '#fff0f0',
  amarelo:  '#d97706', amlBg:    '#fffbeb',
  // r182: amarelo do status 'Pendente de Retorno' (o COR.amarelo do Pausado
  //       e mais ambar -- os dois precisam ser distinguiveis lado a lado).
  amareloRetorno: '#eab308', amlRetBg: '#fefce8',
  roxo:     '#7c3aed', roxoBg:   '#f3e8ff',
  laranja:  '#f97316', laranjaBg:'#fff7ed',
  cinza:    '#6b7280', cinzaSt:  '#f3f4f6',
  // r178: cinza exclusivo do status 'Nao Iniciado' (COR.cinza segue valendo
  //       pro resto -- este e claro demais pra texto sobre branco).
  cinzaNaoIniciado: '#CCCCCC',
  // r219: amarelo da pasta de projeto. Mesmo hex do amareloRetorno, mas com
  //       chave propria de proposito -- aquele e exclusivo do status
  //       'Pendente de Retorno' e nao deve ser reaproveitado.
  amareloPasta: '#EAB308',
  // r220: cores dos status Pausado (azul claro) e Pendente Cham. Filho (laranja).
  //       Chaves proprias — nao reaproveitar COR.azul/COR.laranja, que servem
  //       ao resto da tela.
  azulClaroPausado: '#38BDF8', azulClaroPausadoBg: '#f0f9ff',
  laranjaFilho:     '#F97316', laranjaFilhoBg:     '#fff7ed',
  // r221: nome do projeto no card.
  azulProjeto: '#0070C0',
};

const inputStyle = {
  width: '100%', height: '36px', padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '13px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
};

const selectStyle = { ...inputStyle, cursor: 'pointer' };

// ─── Mapas de status/setor/prioridade ───────────────────────────────────────
const STATUS_EXECUCAO = {
  N: { label: 'Não Iniciado',          cor: COR.cinzaNaoIniciado,    bg: COR.cinzaSt   },
  E: { label: 'Em Andamento',           cor: COR.verdeAndamento,    bg: COR.verdeBg  },
  X: { label: 'Pausado',                 cor: COR.azulClaroPausado, bg: COR.azulClaroPausadoBg },
  R: { label: 'Pendente de Retorno',    cor: COR.amareloRetorno, bg: COR.amlRetBg },
  P: { label: 'Pendente Chamado Filho', cor: COR.laranjaFilho, bg: COR.laranjaFilhoBg },
  F: { label: 'Finalizado',              cor: COR.vermelho, bg: COR.vermBg  },
  S: { label: 'Sandbox',                cor: COR.laranja,  bg: COR.laranjaBg },
};

const SETOR = {
  C: { label: 'Comercial',     cor: '#2563eb' },
  T: { label: 'Técnico',        cor: '#7c3aed' },
  Q: { label: 'Qualidade',      cor: '#f97316' },
  A: { label: 'Administrativo', cor: '#6b7280' },
  P: { label: 'Apoio Técnico',  cor: '#db2777' },
  O: { label: 'Outros',         cor: '#06b6d4' },
};

const PRIORIDADE = {
  A: { label: 'Alta',  cor: COR.vermelho },
  M: { label: 'Média', cor: COR.amarelo  },
  B: { label: 'Baixa', cor: COR.verde    },
};

// ─── Helpers de formato ─────────────────────────────────────────────────────
function formatarData(d) {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('pt-BR');
}

// r178: 'dd/mm/aaaa - hh:mm' — formato do tooltip de solicitação de prioridade
function formatarDataHora(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  return `${dt.toLocaleDateString('pt-BR')} - ${hh}:${mm}`;
}

// ─── Pill genérico (status/setor/prioridade) ────────────────────────────────
function Pill({ texto, cor, bg, icone, compacto }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      // r177: 'compacto' casa exatamente com a pilula de prioridade (altura 20,
      //       fonte 10) pra as duas ficarem do mesmo tamanho no card.
      ...(compacto
        ? { height: '20px', padding: '0 9px', boxSizing: 'border-box' }
        : { padding: '2px 10px' }),
      borderRadius: '20px',
      fontSize: compacto ? '10px' : '12px', fontWeight: compacto ? '700' : '500',
      backgroundColor: bg || `${cor}15`,
      color: cor,
      border: `1px solid ${cor}`,             // r111: borda sólida na cor (era 33 alpha)
      whiteSpace: 'nowrap',
    }}>
      {icone && <span>{icone}</span>}
      {texto}
    </span>
  );
}

// ─── TagCliente — tag colorida do cliente (mesma lógica do ClientesPage) ────
function TagCliente({ tag, cor, titulo }) {
  if (!tag) return null;
  const bgCor = cor || '#003399';
  const hex = bgCor.replace('#', '');
  const r = parseInt(hex.substr(0,2),16) || 0;
  const g = parseInt(hex.substr(2,2),16) || 0;
  const b = parseInt(hex.substr(4,2),16) || 0;
  const lum = (0.299*r + 0.587*g + 0.114*b) / 255;
  const corTexto = lum > 0.5 ? '#1a1a2e' : '#ffffff';
  // r221: mesma fonte/tamanho da pílula de status (Manrope 11px 700, altura 20)
  return (
    <span title={titulo} style={{
      // r222: lineHeight 1 + 1px no topo — a tag costuma ser só maiúsculas
      //       (sem descendentes), e no line-height herdado ela subia na pílula.
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      height: '20px', padding: '1px 9px 0', boxSizing: 'border-box',
      lineHeight: 1,
      borderRadius: '20px', fontSize: '11px', fontWeight: '700',
      fontFamily: "'Manrope', sans-serif",
      backgroundColor: bgCor, color: corTexto,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>{tag}</span>
  );
}

// ─── Card de uma tarefa ─────────────────────────────────────────────────────
// ─── Menu flutuante de prioridade (clica na tag, abre opções) ───────────────
const PRIORIDADE_OPCOES = [
  { v: 'A', label: 'Alta',  cor: '#dc2626', icone: '▲' },
  { v: 'M', label: 'Média', cor: '#d97706', icone: '▬' },
  { v: 'B', label: 'Baixa', cor: '#10b981', icone: '▼' },
];

function PrioridadeMenuInline({ valor, onSelecionar }) {
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setAberto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const atual = PRIORIDADE_OPCOES.find(o => o.v === valor) || PRIORIDADE_OPCOES[2];

  return (
    <div ref={containerRef} style={{ position: 'relative' }}
         onClick={(e) => e.stopPropagation()}>
      <button
        type="button" onClick={() => setAberto(o => !o)}
        title="Alterar prioridade"
        style={{
          height: '20px', padding: '0 9px', borderRadius: '10px',
          fontSize: '10px', fontWeight: '700',
          border: `1px solid ${atual.cor}`,
          backgroundColor: atual.cor, color: '#fff',
          cursor: 'pointer', whiteSpace: 'nowrap', boxSizing: 'border-box',
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          fontFamily: "'Manrope', sans-serif",
        }}
      >
        <span>{atual.icone}</span>
        <span>{atual.label}</span>
      </button>
      {aberto && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 50,
          backgroundColor: '#fff',
          border: `1px solid #e8eaed`, borderRadius: '5px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          minWidth: '120px', overflow: 'hidden',
        }}>
          {PRIORIDADE_OPCOES.map(o => (
            <div key={o.v}
                 onClick={() => { onSelecionar(o.v); setAberto(false); }}
                 style={{
                   padding: '7px 12px', cursor: 'pointer',
                   display: 'flex', alignItems: 'center', gap: '8px',
                   color: o.cor, fontWeight: '700', fontSize: '11px',
                   backgroundColor: valor === o.v ? o.cor + '10' : '#fff',
                 }}
                 onMouseEnter={e => e.currentTarget.style.backgroundColor = o.cor + '20'}
                 onMouseLeave={e => e.currentTarget.style.backgroundColor =
                   valor === o.v ? o.cor + '10' : '#fff'}
            >
              <span style={{ fontSize: '12px' }}>{o.icone}</span>
              <span>{o.label}</span>
              {valor === o.v && <span style={{ marginLeft: 'auto' }}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Menu flutuante de status (clica na pílula, abre opções) ────────────────
// Mesmas regras do modal: Sandbox fica oculto (é só técnico) e 'Finalizado'
// só aparece pra quem pode finalizar (responsável ou admin) — o backend
// também barra (403).
function StatusMenuInline({ valor, podeFinalizar, onSelecionar, desabilitado }) {
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setAberto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const atual = STATUS_EXECUCAO[valor] || STATUS_EXECUCAO.N;
  const opcoes = Object.entries(STATUS_EXECUCAO)
    .filter(([v]) => v !== 'S' || valor === 'S')
    .filter(([v]) => v !== 'F' || podeFinalizar || valor === 'F');

  return (
    <div ref={containerRef} style={{ position: 'relative' }}
         onClick={(e) => e.stopPropagation()}>
      <button
        type="button" onClick={() => { if (!desabilitado) setAberto(o => !o); }}
        title={desabilitado ? `Status: ${atual.label}` : 'Alterar status'}
        style={{
          // retangular com cantos levemente arredondados; fonte 1pt acima da prioridade
          height: '20px', padding: '0 9px', borderRadius: '4px',
          fontSize: '11px', fontWeight: '700',
          border: `1px solid ${atual.cor}`,
          // 'Não Iniciado' é cinza claro — texto branco some, usa escuro
          backgroundColor: atual.cor, color: valor === 'N' || !valor ? '#262626' : '#fff',
          cursor: desabilitado ? 'default' : 'pointer',
          whiteSpace: 'nowrap', boxSizing: 'border-box',
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          fontFamily: "'Manrope', sans-serif",
        }}
      >
        <span>{atual.label}</span>
        {!desabilitado && <span style={{ fontSize: '8px' }}>▾</span>}
      </button>
      {aberto && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 50,
          backgroundColor: '#fff',
          border: `1px solid #e8eaed`, borderRadius: '5px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          minWidth: '170px', overflow: 'hidden',
        }}>
          {opcoes.map(([v, o]) => (
            <div key={v}
                 onClick={() => { setAberto(false); if (v !== valor) onSelecionar(v); }}
                 style={{
                   padding: '7px 12px', cursor: 'pointer',
                   display: 'flex', alignItems: 'center', gap: '8px',
                   color: o.cor, fontWeight: '700', fontSize: '11px',
                   backgroundColor: valor === v ? o.cor + '10' : '#fff',
                 }}
                 onMouseEnter={e => e.currentTarget.style.backgroundColor = o.cor + '20'}
                 onMouseLeave={e => e.currentTarget.style.backgroundColor =
                   valor === v ? o.cor + '10' : '#fff'}
            >
              <span style={{
                width: '8px', height: '8px', borderRadius: '50%',
                backgroundColor: o.cor, flexShrink: 0,
              }} />
              <span>{o.label}</span>
              {valor === v && <span style={{ marginLeft: 'auto' }}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Editor inline de deadline (popover) ─────────────────────────────────────
function DeadlineInline({ valor, atrasada, diasAtraso = 0, onSalvar }) {
  const [aberto, setAberto] = useState(false);
  const [data, setData] = useState(valor ? valor.slice(0, 10) : '');
  const containerRef = useRef(null);

  useEffect(() => {
    if (aberto) setData(valor ? new Date(valor).toISOString().slice(0, 10) : '');
  }, [aberto, valor]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setAberto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const salvar = async () => {
    await onSalvar(data || null);
    setAberto(false);
  };
  const limpar = async () => {
    await onSalvar(null);
    setAberto(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}
         onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setAberto(o => !o)} title="Definir deadline"
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          padding: '2px 4px', borderRadius: '3px',
          fontSize: '12px',
          color: atrasada ? '#dc2626' : '#888',
          fontWeight: atrasada ? '600' : '400',
          fontFamily: "'Manrope', sans-serif",
        }}
      >
        {valor ? new Date(valor).toLocaleDateString('pt-BR') : 'sem deadline'}
        {diasAtraso > 0 && (
          <span title={`${diasAtraso} dia${diasAtraso > 1 ? 's' : ''} em atraso`}
            style={{
              marginLeft: '5px', minWidth: '17px', height: '17px', padding: '0 4px',
              borderRadius: '9px', backgroundColor: '#dc2626', color: '#fff',
              fontSize: '10px', fontWeight: '700',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>{diasAtraso > 999 ? '999+' : diasAtraso}</span>
        )}
      </button>
      {aberto && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 50,
          backgroundColor: '#fff', border: `1px solid #e8eaed`,
          borderRadius: '5px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          padding: '10px', display: 'flex', flexDirection: 'column', gap: '6px',
          minWidth: '180px',
        }}>
          <input type="date" value={data} onChange={e => setData(e.target.value)}
            style={{
              padding: '6px 8px', border: '1px solid #e8eaed', borderRadius: '4px',
              fontSize: '12px', fontFamily: "'Manrope', sans-serif",
            }} />
          <div style={{ display: 'flex', gap: '4px' }}>
            <button onClick={salvar} style={{
              flex: 1, padding: '5px', fontSize: '11px', fontWeight: '600',
              backgroundColor: '#003399', color: '#fff', border: 'none',
              borderRadius: '4px', cursor: 'pointer',
            }}>Salvar</button>
            {valor && (
              <button onClick={limpar} title="Remover deadline" style={{
                padding: '5px 8px', fontSize: '11px',
                backgroundColor: '#fff', color: '#dc2626',
                border: '1px solid #dc2626', borderRadius: '4px', cursor: 'pointer',
              }}>×</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Ícones SVG do card (r176) ──────────────────────────────────────────────
// Inline e sem lib: o card repete por linha e cada ícone é um punhado de paths.
const Svg = ({ children, tamanho = 15, cor = 'currentColor', ...rest }) => (
  <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none"
       stroke={cor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
       style={{ flexShrink: 0 }} {...rest}>
    {children}
  </svg>
);
const IconeSolicitante = (p) => (
  <Svg {...p}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Svg>
);
const IconeResponsavel = (p) => (
  <Svg {...p}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <path d="M17 11l2 2 4-4" />
  </Svg>
);
// r216: ticket — nº do chamado no sistema do cliente
const IconeChamado = (p) => (
  <Svg {...p}>
    <path d="M3 9V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 6v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-6z" />
    <path d="M13 5v14" strokeDasharray="2 2" />
  </Svg>
);
// r216: calendário — data de inclusão
const IconeCalendario = (p) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </Svg>
);
// r216: seta circular — data da última atualização
const IconeAtualizado = (p) => (
  <Svg {...p}>
    <path d="M21 12a9 9 0 1 1-3-6.7" />
    <path d="M21 3v5h-5" />
  </Svg>
);
const IconeCronometro = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="13" r="8" />
    <path d="M12 9v4l2 2" />
    <path d="M9 2h6" />
  </Svg>
);
const IconeBandeira = (p) => (
  <Svg {...p}>
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <path d="M4 22v-7" />
  </Svg>
);
const IconeSino = (p) => (
  <Svg {...p}>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </Svg>
);
// r217: pasta sólida — mesmo desenho de antes, com o preenchimento na mesma
//        cor da borda (o wrapper <Svg> define fill="none", então o fill vai
//        no próprio path).
const IconePasta = (p) => (
  <Svg {...p}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"
          fill={p.cor || 'currentColor'} />
  </Svg>
);
const IconeEditar = (p) => (
  <Svg {...p}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </Svg>
);
const IconeLixeira = (p) => (
  <Svg {...p}>
    <path d="M3 6h18" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </Svg>
);

// ─── Horas decimais → HH:MM ────────────────────────────────────────────────
// O banco guarda numeric(5,2): 6.5 é seis horas e meia, não 6h50. Arredonda no
// minuto pra 6.499999 não virar 06:29.
function horasParaHHMM(dec) {
  const n = Number(dec) || 0;
  const totalMin = Math.round(n * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// ─── Dias de atraso do deadline (0 = no prazo) ─────────────────────────────
// Compara só a data, sem hora: vencer "hoje" ainda não é atraso.
function diasDeAtraso(deadline, statusExec) {
  if (!deadline || statusExec === 'F') return 0;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const d = new Date(deadline);
  d.setHours(0, 0, 0, 0);
  const dias = Math.floor((hoje - d) / 86400000);
  return dias > 0 ? dias : 0;
}

// ─── Card de uma tarefa — linha densa de 2 níveis (r176) ────────────────────
// Layout do modelo "Tarefas Layout 2": colunas fixas à direita e o bloco de
// identificação ocupando a sobra. A cor de tudo (borda + faixa de ações) é a do
// STATUS — não existe mais pill de status, o nome dele vai no title da linha.
function CardTarefa({ tarefa, onAbrir, onEditar, onExcluir, onAtualizarCampos, onAlterarStatus,
                      podeAlterar, ehAdmin, usuarioLogadoId }) {
  const status = STATUS_EXECUCAO[tarefa.status_execucao] || STATUS_EXECUCAO.N;
  const setor  = SETOR[tarefa.setor] || SETOR.O;
  const naoLida = !tarefa.visualizado_por_responsavel && tarefa.responsavel_id === usuarioLogadoId;
  const atraso  = diasDeAtraso(tarefa.deadline, tarefa.status_execucao);
  const naoLidos = Number(tarefa.comentarios_nao_lidos) || 0;
  // r178: solicitação de priorização registrada — vira linha no tooltip do sino.
  const avisoPrioridade = tarefa.prioridade_solicitada_em
    ? `Prioridade solicitada por ${tarefa.prioridade_solicitada_por_nome || 'usuário'}`
      + ` em ${formatarDataHora(tarefa.prioridade_solicitada_em)}`
    : '';
  const apontadas = Number(tarefa.horas_apontadas) || 0;
  const estouro = tarefa.horas_estimadas && apontadas > Number(tarefa.horas_estimadas);

  // Cada coluna são duas linhas de 18px. Fixar a altura mantém tudo alinhado
  // mesmo quando falta cliente, projeto ou responsável.
  const linha = {
    // r177: 20px acomoda as pilulas (prioridade e setor tem 20 de altura) sem
    //       elas transbordarem a linha.
    height: '20px', display: 'flex', alignItems: 'center', gap: '6px',
    fontSize: '12px', color: COR.texto, minWidth: 0,
  };
  const coluna = { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 };
  const truncar = {
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
  };

  return (
    <div
      onClick={() => onAbrir(tarefa)}
      title={`Status: ${status.label}`}
      style={{
        display: 'flex', alignItems: 'stretch',
        backgroundColor: COR.branco,
        border: `2px solid ${status.cor}`,
        borderRadius: '8px',
        // r177: SEM overflow:hidden. Ele cortava o popover de prioridade, que
        //       abre pra fora do card -- z-index nao resolve isso, quem corta e
        //       o overflow do pai. Os cantos das faixas coloridas sao
        //       arredondados individualmente no lugar.
        cursor: 'pointer',
        transition: 'box-shadow 0.15s',
        boxShadow: naoLida ? `0 0 0 3px ${status.cor}22` : 'none',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 2px 10px ${COR.borda}`; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = naoLida ? `0 0 0 3px ${status.cor}22` : 'none'; }}
    >
      {/* Barra larga de status (r177) — o traço grosso do modelo, à esquerda */}
      <div style={{
        width: '13.5px', flexShrink: 0,   // r178: +50% sobre os 9px iniciais
        backgroundColor: status.cor,
        borderRadius: '6px 0 0 6px',
      }} />

      {/* Conteúdo — colunas de largura fixa à direita, identificação na sobra */}
      <div style={{
        flex: 1, minWidth: 0,
        display: 'grid',
        // r216: coluna de datas (inclusão / última atualização) entra entre o
        //       bloco de solicitante e o de horas+deadline.
        // r216c: o nº do chamado voltou pra dentro do bloco de identificação,
        //        logo depois do título — coluna própria deixava ele solto no
        //        meio do card.
        // Coluna de status (editável) entre horas+deadline e prioridade+setor.
        gridTemplateColumns: 'minmax(0, 1fr) minmax(140px, 200px) 122px 128px 150px 108px 34px',
        alignItems: 'center', gap: '14px',
        padding: '9px 12px',
      }}>
        {/* COLUNA 1 — #id | título / cliente + projeto */}
        <div style={coluna}>
          <div style={linha}>
            {/* r178: cor fixa, a mesma do titulo — o status ja e comunicado
                pela barra, pela borda e pela faixa de acoes. */}
            <span style={{ fontSize: '14px', fontWeight: '800', color: COR.texto, flexShrink: 0 }}>
              #{tarefa.id}
            </span>
            <span style={{ color: COR.borda, flexShrink: 0 }}>|</span>
            <span style={{ fontSize: '14px', fontWeight: '800', ...truncar }}>
              {tarefa.titulo}
            </span>
            {/* r216c: nº do chamado do cliente, logo depois do título.
                flexShrink 0 — quem encolhe quando falta espaço é o título. */}
            {tarefa.chamado && (
              <span title={`Chamado do cliente: ${tarefa.chamado}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      flexShrink: 0, maxWidth: '180px', marginLeft: '2px',
                      color: COR.azul, fontSize: '14px', fontWeight: '700',
                    }}>
                <IconeChamado tamanho={15} cor={COR.azul} />
                <span style={truncar}>{tarefa.chamado}</span>
              </span>
            )}
            {naoLida && (
              <span title="Você ainda não visualizou esta tarefa"
                    style={{ color: COR.vermelho, fontSize: '15px', flexShrink: 0 }}>●</span>
            )}
          </div>
          <div style={{ ...linha, color: COR.muted, fontSize: '11px' }}>
            {tarefa.cliente_tag
              ? <TagCliente tag={tarefa.cliente_tag} cor={tarefa.cliente_cor_tag}
                            titulo={tarefa.cliente_fantasia || tarefa.cliente_nome} />
              : <span style={{ fontStyle: 'italic' }}>sem cliente</span>}
            {/* r217: 12px — 1pt acima do resto da linha, pra descrição do
                projeto ganhar peso sobre a tag do cliente. */}
            {tarefa.negocio_id ? (
              <span style={{ ...linha, ...truncar, color: COR.texto, fontSize: '12px' }}>
                <IconePasta tamanho={14} cor={COR.amareloPasta} />
                {/* r222: nome do projeto em Manrope Semibold, azul #0070C0 */}
                <span style={{
                  ...truncar, color: COR.azulProjeto, fontWeight: '600',
                  fontFamily: "'Manrope', sans-serif",
                }}>
                  {tarefa.negocio_id}
                  {tarefa.negocio_descricao && ` | ${tarefa.negocio_descricao}`}
                </span>
              </span>
            ) : (
              <span style={{ fontStyle: 'italic' }}>sem projeto</span>
            )}
          </div>
        </div>

        {/* COLUNA 2 — solicitante / responsável */}
        <div style={coluna}>
          {/* r182: nome na cor do próprio ícone e em negrito */}
          <div style={{ ...linha, color: COR.muted }} title="Solicitante">
            <IconeSolicitante cor={COR.roxo} />
            <span style={{ ...truncar, color: COR.roxo, fontWeight: '700' }}>
              {tarefa.solicitante_nome || '—'}
            </span>
          </div>
          <div style={{ ...linha, color: COR.muted }} title="Responsável">
            <IconeResponsavel cor={COR.verdeHoras} />
            <span style={{
              ...truncar,
              color: tarefa.responsavel_nome ? COR.verdeHoras : COR.muted,
              fontWeight: tarefa.responsavel_nome ? '700' : '400',
              fontStyle: tarefa.responsavel_nome ? 'normal' : 'italic',
            }}>
              {tarefa.responsavel_nome || 'sem responsável'}
            </span>
          </div>
        </div>

        {/* COLUNA 3 (r216) — data de inclusão / última atualização.
            "Última atualização" vem calculada do backend: o mais recente entre
            alteração da tarefa, comentário, apontamento de horas e criação de
            sub-tarefa. */}
        <div style={coluna}>
          <div style={{ ...linha, color: COR.muted, fontSize: '11px' }}
               title={`Criada em ${formatarDataHora(tarefa.criado_em) || '—'}`}>
            <IconeCalendario tamanho={13} cor={COR.muted} />
            <span style={truncar}>{formatarData(tarefa.criado_em) || '—'}</span>
          </div>
          <div style={{ ...linha, color: COR.muted, fontSize: '11px' }}
               title={`Última atualização em ${formatarDataHora(tarefa.ultima_atualizacao) || '—'}`
                      + ' (alteração, comentário, apontamento ou sub-tarefa)'}>
            <IconeAtualizado tamanho={13} cor={COR.azul} />
            <span style={{ ...truncar, color: COR.azul, fontWeight: '600' }}>
              {formatarData(tarefa.ultima_atualizacao) || '—'}
            </span>
          </div>
        </div>

        {/* COLUNA 4 — horas apontadas / deadline + dias de atraso */}
        <div style={coluna}>
          <div style={linha}
               title={tarefa.horas_estimadas
                 ? `Apontadas ${horasParaHHMM(apontadas)} de ${horasParaHHMM(tarefa.horas_estimadas)} estimadas`
                 : `Horas apontadas: ${horasParaHHMM(apontadas)}`}>
            <IconeCronometro cor={estouro ? COR.vermelho : COR.verdeHoras} />
            <span style={{ fontWeight: '700', color: estouro ? COR.vermelho : COR.verdeHoras }}>
              {horasParaHHMM(apontadas)}
            </span>
          </div>
          <div style={linha}>
            <IconeBandeira cor={atraso > 0 ? COR.vermelho : COR.muted} />
            <DeadlineInline
              valor={tarefa.deadline}
              atrasada={atraso > 0}
              diasAtraso={atraso}
              onSalvar={async (nova) => { await onAtualizarCampos(tarefa.id, { deadline: nova }); }}
            />
          </div>
        </div>

        {/* COLUNA 5 — status (editável pra quem pode alterar tarefa) */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <StatusMenuInline
            valor={tarefa.status_execucao}
            desabilitado={!podeAlterar}
            podeFinalizar={ehAdmin || tarefa.responsavel_id === usuarioLogadoId}
            onSelecionar={(v) => onAlterarStatus(tarefa, v)}
          />
        </div>

        {/* COLUNA 6 — prioridade (editável) / setor */}
        <div style={{ ...coluna, alignItems: 'flex-start' }}>
          <div style={linha}>
            <PrioridadeMenuInline
              valor={tarefa.prioridade}
              onSelecionar={async (v) => {
                const campos = { prioridade: v };
                // r178: responsável é outra pessoa? oferece o alerta de solicitação.
                if (v !== tarefa.prioridade
                    && tarefa.responsavel_id
                    && tarefa.responsavel_id !== usuarioLogadoId) {
                  campos.solicitar_prioridade = window.confirm(
                    `Deseja gerar um alerta de solicitação de prioridade para `
                    + `${tarefa.responsavel_nome || 'o responsável'}?`
                  );
                }
                await onAtualizarCampos(tarefa.id, campos);
              }}
            />
          </div>
          <div style={linha}>
            <Pill texto={setor.label} cor={setor.cor} compacto />
          </div>
        </div>

        {/* COLUNA 7 — sino de comentários não lidos. Largura fixa mesmo vazio,
            senão as colunas dançam de linha pra linha. */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {(naoLidos > 0 || avisoPrioridade) && (
            <span
              title={[
                naoLidos > 0
                  ? `${naoLidos} comentário${naoLidos > 1 ? 's' : ''} não lido${naoLidos > 1 ? 's' : ''}`
                  : '',
                avisoPrioridade,
              ].filter(Boolean).join('\n')}
              style={{ position: 'relative', display: 'inline-flex', color: status.cor }}
            >
              <IconeSino tamanho={20} />
              {naoLidos > 0 && <span style={{
                position: 'absolute', top: '-5px', right: '-7px',
                minWidth: '15px', height: '15px', padding: '0 3px',
                borderRadius: '8px', backgroundColor: status.cor, color: COR.branco,
                fontSize: '9px', fontWeight: '700',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1.5px solid ${COR.branco}`, boxSizing: 'content-box',
              }}>{naoLidos > 99 ? '99+' : naoLidos}</span>}
            </span>
          )}
        </div>
      </div>

      {/* Faixa de ações — na cor do status, ícones brancos empilhados */}
      <div style={{
        width: '38px', flexShrink: 0,
        backgroundColor: status.cor,
        borderRadius: '0 6px 6px 0',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: '2px',
      }}>
        <SePode rotina="tarefas" acao="alterar">
          <button
            onClick={(e) => { e.stopPropagation(); onEditar(tarefa); }}
            title="Editar tarefa"
            style={{
              border: 'none', background: 'transparent', color: COR.branco,
              cursor: 'pointer', padding: '3px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          ><IconeEditar tamanho={17} /></button>
        </SePode>
        <SePode rotina="tarefas" acao="excluir">
          <button
            onClick={(e) => { e.stopPropagation(); onExcluir(tarefa); }}
            title="Excluir tarefa"
            style={{
              border: 'none', background: 'transparent', color: COR.branco,
              cursor: 'pointer', padding: '3px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          ><IconeLixeira tamanho={17} /></button>
        </SePode>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Component principal
// ════════════════════════════════════════════════════════════════════════════
// ─── Chave do localStorage pra persistir filtros (Fase 3) ──────────────────
// Inclui usuario.id pra cada user ter seu próprio set.
const STORAGE_KEY = (uid) => `tarefas.filtros.${uid || 'anon'}`;

// Filtros default
const FILTROS_DEFAULT = {
  busca:            '',
  // multi-valor (arrays de strings)
  status_execucao:  ['N','E','X','P'],  // não-finalizadas por padrão
  setor:            [],
  prioridade:       [],
  cliente_ids:      [],
  negocio_ids:      [],
  solicitante_ids:  [],
  responsavel_ids:  [],
  // datas (string YYYY-MM-DD ou vazia)
  data_prog_de:     '',
  data_prog_ate:    '',
  deadline_de:      '',
  deadline_ate:     '',
  // prefixado: 'atribuidas' | 'criadas' | 'criadas_por_outros' | 'criadas_para_outros' | 'citadas' | 'todas'
  papel:            'atribuidas',
  // ordenação
  ordem:            'recente',
};

// ─── <MultiSelect> — dropdown com checkboxes pra Fase 3 ──────────────────────
// Props:
//   label:    string mostrado no botão quando vazio
//   opcoes:   [{v: 'A', label: 'Alta', cor?: '#xxx'}]
//   valores:  array de v's selecionados
//   onChange: (novoArray) => void
//   largura:  width opcional (default 140px)
//   compacto: usa altura 28px (versão da barra de filtros)
// ─────────────────────────────────────────────────────────────────────────────
//   resumoContagem: fechado, mostra "Label: NN Selec." em vez dos itens (r222)
function MultiSelect({ label, opcoes, valores, onChange, largura, compacto, resumoContagem }) {
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setAberto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleValor = (v) => {
    if (valores.includes(v)) onChange(valores.filter(x => x !== v));
    else onChange([...valores, v]);
  };

  const limpar = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  // Texto do botão: se há ≤2 itens, mostra; se há mais, mostra "X selecionados"
  let textoBotao;
  if (valores.length === 0) textoBotao = label;
  else if (resumoContagem) textoBotao = `${label}: ${String(valores.length).padStart(2, '0')} Selec.`;
  else if (valores.length === 1) {
    const op = opcoes.find(o => o.v === valores[0]);
    textoBotao = op?.label || valores[0];
  }
  else if (valores.length === 2) {
    textoBotao = valores.map(v => opcoes.find(o => o.v === v)?.label || v).join(', ');
  }
  else {
    textoBotao = `${valores.length} selecionados`;
  }

  const temSelecao = valores.length > 0;
  const h = compacto ? '28px' : '32px';

  return (
    <div ref={containerRef} style={{ position: 'relative', width: largura || '140px' }}>
      <button
        type="button"
        onClick={() => setAberto(a => !a)}
        style={{
          width: '100%', height: h,
          padding: '0 8px',
          paddingRight: temSelecao ? '24px' : '20px',
          border: `1px solid ${temSelecao ? COR.azul : COR.borda}`,
          borderRadius: '4px',
          backgroundColor: temSelecao ? COR.azulBg : COR.branco,
          color: temSelecao ? COR.azul : COR.texto,
          fontSize: '11px', fontWeight: temSelecao ? '600' : '500',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '4px',
          fontFamily: "'Manrope', sans-serif",
          textAlign: 'left',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          position: 'relative',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
          {textoBotao}
        </span>
        <span style={{ fontSize: '8px', flexShrink: 0 }}>▾</span>
        {temSelecao && (
          <span
            role="button"
            onClick={limpar}
            style={{
              position: 'absolute', right: '4px', top: '50%',
              transform: 'translateY(-50%)',
              width: '14px', height: '14px', borderRadius: '50%',
              backgroundColor: COR.azul, color: COR.branco,
              fontSize: '9px', fontWeight: '700',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
            title="Limpar seleção"
          >×</span>
        )}
      </button>

      {aberto && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0,
          minWidth: '100%', maxWidth: '320px',
          maxHeight: '300px',
          backgroundColor: COR.branco,
          border: `1px solid ${COR.borda}`, borderRadius: '5px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 100,
          overflowY: 'auto',
        }}>
          {opcoes.length === 0 ? (
            <div style={{
              padding: '10px 14px', fontSize: '11px', color: COR.muted,
              fontStyle: 'italic',
            }}>
              Sem opções.
            </div>
          ) : opcoes.map(op => {
            const marcado = valores.includes(op.v);
            return (
              <label key={op.v} style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '7px 12px', cursor: 'pointer',
                fontSize: '12px', color: COR.texto,
                backgroundColor: marcado ? COR.azulBg : COR.branco,
                borderBottom: `1px solid ${COR.borda}`,
              }}
              onMouseEnter={e => { if (!marcado) e.currentTarget.style.backgroundColor = COR.cinzaBg; }}
              onMouseLeave={e => { if (!marcado) e.currentTarget.style.backgroundColor = COR.branco; }}
              >
                <input
                  type="checkbox"
                  checked={marcado}
                  onChange={() => toggleValor(op.v)}
                  style={{ cursor: 'pointer' }}
                />
                {op.cor && (
                  <span style={{
                    width: '10px', height: '10px', borderRadius: '50%',
                    backgroundColor: op.cor, flexShrink: 0,
                  }} />
                )}
                <span style={{
                  flex: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {op.label}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TarefasPageLista({ onAlternarModo }) {
  const { usuario } = useAuth();
  const { isAdmin, isBootstrap, pode } = usePermissoes();   // status inline no card
  const [tarefas, setTarefas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [painel, setPainel] = useState(null);   // 'novo' | 'visualizar' | 'editar'
  const [selecionada, setSelecionada] = useState(null);

  // r186: alerta de notificações não lidas — reaparece a cada ação da tela,
  //       a menos que o usuário tenha silenciado (+30min/+1h/+4h/+1dia).
  const alerta = useAlertaNotificacoes(usuario);
  const { verificar: verificarNotificacoes } = alerta;

  // r186: deep link vindo do sininho — /tarefas?tarefa_id=123 abre o modal.
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Filtros (Fase 3) ────────────────────────────────────────────────────
  // Inicia do localStorage; se não houver, usa default.
  const [filtros, setFiltros] = useState(() => {
    if (!usuario?.id) return FILTROS_DEFAULT;
    try {
      const raw = localStorage.getItem(STORAGE_KEY(usuario.id));
      if (raw) {
        const persistido = JSON.parse(raw);
        return normalizarFiltros({ ...FILTROS_DEFAULT, ...persistido, busca: '' });
      }
    } catch (err) {
      console.warn('Erro ao carregar filtros do localStorage:', err);
    }
    return FILTROS_DEFAULT;
  });

  // Toggle pra expandir/recolher a linha de filtros avançados
  const [filtrosExpandidos, setFiltrosExpandidos] = useState(false);

  // r221: a busca só dispara ao clicar na lupa (ou Enter). `textoBusca` é o que
  //       está digitado; `filtros.busca` é o que foi aplicado. Com "Considerar
  //       Filtros" desmarcado (padrão), a busca ignora papel/status/demais
  //       filtros e considera só o texto. A busca não é persistida.
  const [textoBusca, setTextoBusca] = useState('');
  const [considerarFiltros, setConsiderarFiltros] = useState(false);
  const [buscaComFiltros, setBuscaComFiltros] = useState(false);

  // ── Listas pra os comboboxes (Cliente, Projeto, Solicitante, Responsável)
  // Derivamos dos próprios resultados das tarefas pra ficar simples (sem
  // queries adicionais). Quando muda a base de tarefas, o select se ajusta.
  const opcoesClientes = useMemo(() => {
    const map = new Map();
    tarefas.forEach(t => {
      if (t.cliente_id && !map.has(t.cliente_id)) {
        map.set(t.cliente_id, t.cliente_fantasia || t.cliente_nome || t.cliente_id);
      }
    });
    return Array.from(map.entries()).map(([v, label]) => ({ v, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tarefas]);

  const opcoesProjetos = useMemo(() => {
    const map = new Map();
    tarefas.forEach(t => {
      if (t.negocio_id && !map.has(t.negocio_id)) {
        map.set(t.negocio_id, `${t.negocio_id}${t.negocio_descricao ? ' — ' + t.negocio_descricao : ''}`);
      }
    });
    return Array.from(map.entries()).map(([v, label]) => ({ v, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tarefas]);

  const opcoesSolicitantes = useMemo(() => {
    const map = new Map();
    tarefas.forEach(t => {
      if (t.solicitante_id && !map.has(t.solicitante_id)) {
        map.set(t.solicitante_id, t.solicitante_nome || t.solicitante_id);
      }
    });
    return Array.from(map.entries()).map(([v, label]) => ({ v, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tarefas]);

  const opcoesResponsaveis = useMemo(() => {
    const map = new Map();
    tarefas.forEach(t => {
      if (t.responsavel_id && !map.has(t.responsavel_id)) {
        map.set(t.responsavel_id, t.responsavel_nome || t.responsavel_id);
      }
    });
    return Array.from(map.entries()).map(([v, label]) => ({ v, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tarefas]);

  // ── Persistência em localStorage quando filtros mudam ───────────────────
  useEffect(() => {
    if (!usuario?.id) return;
    try {
      localStorage.setItem(STORAGE_KEY(usuario.id), JSON.stringify({ ...filtros, busca: '' }));
    } catch (err) {
      console.warn('Erro ao salvar filtros:', err);
    }
  }, [filtros, usuario]);

  // ── Carregar filtros do localStorage QUANDO usuario aparece ─────────────
  // (caso a página monte antes do useAuth resolver)
  const filtrosCarregadosRef = useRef(false);
  useEffect(() => {
    if (!usuario?.id || filtrosCarregadosRef.current) return;
    filtrosCarregadosRef.current = true;
    try {
      const raw = localStorage.getItem(STORAGE_KEY(usuario.id));
      if (raw) {
        const persistido = JSON.parse(raw);
        setFiltros(() => normalizarFiltros({ ...FILTROS_DEFAULT, ...persistido, busca: '' }));
      }
    } catch (err) {
      console.warn('Erro ao carregar filtros:', err);
    }
  }, [usuario]);

  // ── Carrega tarefas: filtros vão TODOS pro backend ──────────────────────
  const carregar = useCallback(async () => {
    if (!usuario) return;
    try {
      setCarregando(true);
      const params = {};

      // r35 item 5: passar usuario_id pro backend aplicar escopo (admin vê tudo)
      params.usuario_id = usuario.id;

      // r171: o recorte de papel é resolvido no backend (ANDado com o filtro
      // do perfil de acesso). 'todas' vai igual — lá ele só não estreita nada.
      // r221: busca aplicada sem "Considerar Filtros" → só o texto conta.
      const soBusca = !!filtros.busca.trim() && !buscaComFiltros;
      params.papel = soBusca ? 'todas' : filtros.papel;

      if (!soBusca) {
        // Multi-valor (CSV)
        if (filtros.status_execucao.length > 0) params.status_execucao = filtros.status_execucao.join(',');
        if (filtros.setor.length > 0)            params.setor           = filtros.setor.join(',');
        if (filtros.prioridade.length > 0)       params.prioridade      = filtros.prioridade.join(',');
        if (filtros.cliente_ids.length > 0)      params.cliente_ids     = filtros.cliente_ids.join(',');
        if (filtros.negocio_ids.length > 0)      params.negocio_ids     = filtros.negocio_ids.join(',');
        if (filtros.solicitante_ids.length > 0)  params.solicitante_ids = filtros.solicitante_ids.join(',');
        if (filtros.responsavel_ids.length > 0)  params.responsavel_ids = filtros.responsavel_ids.join(',');

        // Datas
        if (filtros.data_prog_de)    params.data_prog_de    = filtros.data_prog_de;
        if (filtros.data_prog_ate)   params.data_prog_ate   = filtros.data_prog_ate;
        if (filtros.deadline_de)     params.deadline_de     = filtros.deadline_de;
        if (filtros.deadline_ate)    params.deadline_ate    = filtros.deadline_ate;
      }

      // Busca + ordenação
      if (filtros.busca.trim())    params.busca = filtros.busca.trim();
      if (filtros.ordem)           params.ordem = filtros.ordem;

      const dados = await tarefaService.listar(params);

      setTarefas(dados);
    } catch (err) {
      console.error('Erro ao carregar tarefas:', err);
    } finally {
      setCarregando(false);
    }
  }, [usuario, filtros, buscaComFiltros]);

  // Debounce do carregar pra não consultar a cada tecla na busca
  useEffect(() => {
    const t = setTimeout(() => { carregar(); }, 250);
    return () => clearTimeout(t);
  }, [carregar]);

  // Filtragem local — agora vai pro backend, lista exibida é a "tarefas" direto
  const listaFiltrada = tarefas;

  // Helpers pra mudar filtros
  const atualizarFiltro = (chave, valor) => {
    setFiltros(f => ({ ...f, [chave]: valor }));
    verificarNotificacoes();
  };
  const limparFiltros = () => {
    setFiltros({ ...FILTROS_DEFAULT });
    setTextoBusca('');
    verificarNotificacoes();
  };
  // r221: aplica a busca (lupa / Enter) com o estado atual do "Considerar Filtros"
  const executarBusca = (texto = textoBusca) => {
    setBuscaComFiltros(considerarFiltros);
    setFiltros(f => ({ ...f, busca: texto.trim() }));
    verificarNotificacoes();
  };
  // Quantos filtros estão ativos (pra mostrar badge no botão "Filtros")
  const totalFiltrosAtivos = useMemo(() => {
    let n = 0;
    // status default tem 4 não-finalizados; conta como ativo se for diferente disso
    const statusDefault = FILTROS_DEFAULT.status_execucao;
    if (filtros.status_execucao.length !== statusDefault.length
        || filtros.status_execucao.some(s => !statusDefault.includes(s))) n++;
    if (filtros.setor.length > 0) n++;
    if (filtros.prioridade.length > 0) n++;
    if (filtros.cliente_ids.length > 0) n++;
    if (filtros.negocio_ids.length > 0) n++;
    if (filtros.solicitante_ids.length > 0) n++;
    if (filtros.responsavel_ids.length > 0) n++;
    if (filtros.data_prog_de || filtros.data_prog_ate) n++;
    if (filtros.deadline_de || filtros.deadline_ate) n++;
    return n;
  }, [filtros]);

  // ─── Ações ────────────────────────────────────────────────────────────────
  const abrirNova = () => {
    setSelecionada(null);
    setPainel('novo');
    verificarNotificacoes();
  };

  const abrirTarefa = useCallback(async (t) => {
    setSelecionada(t);
    setPainel('visualizar');

    // Marca como visualizada se eu sou o responsável
    if (t.responsavel_id === usuario.id && !t.visualizado_por_responsavel) {
      try {
        await tarefaService.marcarVisualizado(t.id, usuario.id);
        // Atualiza local sem precisar recarregar do backend
        setTarefas(arr => arr.map(x => x.id === t.id ? { ...x, visualizado_por_responsavel: 1 } : x));
      } catch (err) {
        console.warn('Falha ao marcar visualizado:', err);
      }
    }
  }, [usuario]);

  // Wrapper usado nos cards: abre + dispara a checagem de notificações
  const abrirTarefaDoCard = (t) => {
    abrirTarefa(t);
    verificarNotificacoes();
  };

  // r186: abre o modal a partir do ID (sininho / pop-up de notificações).
  // A tarefa pode não estar na lista atual (filtros), então busca no backend.
  const abrirTarefaPorId = useCallback(async (id) => {
    if (!id || !usuario?.id) return;
    try {
      const t = await tarefaService.buscarPorId(id, usuario.id);
      if (t) await abrirTarefa(t);
    } catch (err) {
      alert(err.response?.data?.mensagem || `Não foi possível abrir a tarefa #${id}.`);
    }
  }, [usuario, abrirTarefa]);

  // r186: consome o ?tarefa_id= da URL e limpa o parâmetro (pra F5 não reabrir).
  const deepLinkRef = useRef(null);
  useEffect(() => {
    const alvo = searchParams.get('tarefa_id');
    if (!alvo || !usuario?.id) return;
    // o `_n` do sininho garante URL nova a cada clique; a chave evita reabrir
    // o modal quando o efeito roda de novo por outra razão.
    const chave = `${alvo}:${searchParams.get('_n') || ''}`;
    if (deepLinkRef.current === chave) return;
    deepLinkRef.current = chave;
    abrirTarefaPorId(alvo);
    const restante = new URLSearchParams(searchParams);
    restante.delete('tarefa_id');
    restante.delete('_n');
    setSearchParams(restante, { replace: true });
  }, [searchParams, setSearchParams, usuario, abrirTarefaPorId]);

  const fecharPainel = () => {
    setPainel(null);
    setSelecionada(null);
  };

  const aoSalvar = async () => {
    fecharPainel();
    await carregar();
    verificarNotificacoes();
  };

  // Abre direto em modo edição (botão no card)
  const editarTarefa = (t) => {
    setSelecionada(t);
    setPainel('editar');
    verificarNotificacoes();
  };

  // Exclui com confirmação (botão no card)
  const excluirTarefa = async (t) => {
    if (!window.confirm(`Excluir a tarefa #${t.id} "${t.titulo}"?`)) return;
    try {
      await tarefaService.excluir(t.id, usuario.id);
      await carregar();
      verificarNotificacoes();
    } catch (err) {
      alert(err.response?.data?.mensagem || 'Erro ao excluir tarefa.');
    }
  };

  // Atualiza apenas campos selecionados (prioridade, deadline, programação) — inline no browse
  // Atualização otimista: aplica local imediatamente, depois sincroniza.
  // Se falhar, recarrega lista pra reverter.
  const atualizarCamposTarefa = async (id, campos) => {
    // r178: `solicitar_prioridade` é flag de comando, não campo da tarefa —
    //       fica fora do merge otimista.
    const { solicitar_prioridade, ...visiveis } = campos;
    setTarefas(arr => arr.map(t => t.id === id ? { ...t, ...visiveis } : t));
    try {
      await tarefaService.atualizarParcial(id, campos, usuario.id);
      // O alerta grava comentário + carimbo: recarrega pro sino/tooltip refletirem.
      if (solicitar_prioridade) await carregar();
      verificarNotificacoes();
    } catch (err) {
      alert(err.response?.data?.mensagem || 'Erro ao atualizar campos.');
      await carregar();  // reverte ao estado real
    }
  };

  // Troca de status direto no card — mesma regra do modal: se houver
  // sub-tarefas abertas o backend devolve 409 e pedimos confirmação.
  const alterarStatusTarefa = async (tarefa, novoStatus, force = false) => {
    try {
      await tarefaService.alterarStatusExecucao(tarefa.id, novoStatus, usuario.id, force);
      await carregar();
      verificarNotificacoes();
    } catch (err) {
      const resp = err.response;
      if (resp?.status === 409 && resp.data?.filhas_abertas) {
        const ok = window.confirm(
          `Esta tarefa tem ${resp.data.filhas_abertas} sub-tarefa(s) em aberto.\n\nConcluir mesmo assim?`
        );
        if (ok) await alterarStatusTarefa(tarefa, novoStatus, true);
        return;
      }
      alert(resp?.data?.mensagem || 'Erro ao alterar status.');
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'Manrope', sans-serif" }}>

      {/* r111: BrowseHeader padronizado no topo */}
      <BrowseHeader
        rotina="tarefas"
        titulo="Tarefas e Pendências"
        descricao="Tarefas atribuídas a você e à equipe, com status, prioridade e prazos."
      />

      {/* r140: toggle Lista/Agenda */}
      {onAlternarModo && (
        <div style={{ marginBottom: '10px' }}>
          <div style={{ display: 'inline-flex', borderRadius: '5px', overflow: 'hidden',
                        border: `1px solid ${COR.borda}` }}>
            {/* r220: ícones das abas em SVG (eram emoji 📋/📅) */}
            <button style={{
              padding: '6px 14px', border: 'none',
              background: COR.azul, color: COR.branco,
              fontSize: '12px', fontWeight: '600', cursor: 'pointer',
              fontFamily: "'Manrope', sans-serif",
              display: 'inline-flex', alignItems: 'center', gap: '6px',
            }}>
              <IconeAbaLista tamanho={14} cor={COR.branco} />
              Lista
            </button>
            <button onClick={() => onAlternarModo('agenda')} style={{
              padding: '6px 14px', border: 'none',
              background: COR.branco, color: COR.muted,
              fontSize: '12px', fontWeight: '500', cursor: 'pointer',
              fontFamily: "'Manrope', sans-serif",
              display: 'inline-flex', alignItems: 'center', gap: '6px',
            }}>
              <IconeAbaAgenda tamanho={14} cor={COR.muted} />
              Agenda
            </button>
          </div>
        </div>
      )}

      {/* ═══ BARRA DE FILTROS — Fase 3 ════════════════════════════════════ */}
      <div style={{
        backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`,
        borderRadius: '8px',
        marginBottom: '12px',
        overflow: 'visible',
      }}>
        {/* ─── LINHA 1: Contagem + Papel + Busca + Ordenação + Ações ──── */}
        <div style={{
          padding: '10px 14px',
          display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
          borderBottom: filtrosExpandidos ? `1px solid ${COR.borda}` : 'none',
        }}>
          <span style={{
            fontSize: '13px', fontWeight: '500', color: COR.muted,
            fontFamily: "'Manrope', sans-serif",
          }}>
            {listaFiltrada.length} registro{listaFiltrada.length !== 1 ? 's' : ''}
          </span>

          {/* Botões prefixados de papel */}
          <div style={{ display: 'flex', gap: '4px', marginLeft: '12px' }}>
            {[
              { v: 'atribuidas',          label: 'Atribuídas a mim' },
              { v: 'criadas',             label: 'Criadas por mim' },
              { v: 'criadas_por_outros',  label: 'Criadas por outros' },
              { v: 'criadas_para_outros', label: 'Criadas para outros' },
              { v: 'citadas',             label: 'Citado' },
              { v: 'todas',               label: 'Todos' },
            ].map(o => {
              const ativo = filtros.papel === o.v;
              return (
                <button
                  key={o.v}
                  onClick={() => atualizarFiltro('papel', o.v)}
                  style={{
                    padding: '5px 10px', fontSize: '11px', fontWeight: '600',
                    border: `1px solid ${ativo ? COR.azul : COR.borda}`,
                    borderRadius: '4px',
                    backgroundColor: ativo ? COR.azul : COR.branco,
                    color: ativo ? COR.branco : COR.texto,
                    cursor: 'pointer',
                    fontFamily: "'Manrope', sans-serif",
                  }}
                >{o.label}</button>
              );
            })}
          </div>

          {/* r221: Status nos filtros rápidos (saiu da linha de avançados) */}
          <MultiSelect
            compacto
            label="Status"
            valores={filtros.status_execucao}
            onChange={v => atualizarFiltro('status_execucao', v)}
            opcoes={Object.entries(STATUS_EXECUCAO).map(([v, info]) => ({
              v, label: info.label, cor: info.cor,
            }))}
            largura="160px"
            resumoContagem
          />

          <div style={{ flex: 1 }} />

          {/* r222: "Considerar Filtros" fica à esquerda do campo de busca */}
          <label title="Se marcado, a busca também aplica os filtros setados (papel, status, etc.)"
                 style={{
                   display: 'inline-flex', alignItems: 'center', gap: '4px',
                   fontSize: '11px', fontWeight: '600', color: COR.texto,
                   cursor: 'pointer', whiteSpace: 'nowrap',
                   fontFamily: "'Manrope', sans-serif",
                 }}>
            <input type="checkbox" checked={considerarFiltros}
                   onChange={e => setConsiderarFiltros(e.target.checked)}
                   style={{ cursor: 'pointer', margin: 0 }} />
            Considerar Filtros
          </label>

          {/* Busca */}
          {/* r221: a lupa virou botão — a busca só roda ao clicar nela (ou
              Enter). Limpar o campo restaura a lista na hora. */}
          <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <input type="text" placeholder="Buscar..." value={textoBusca}
                   onChange={e => {
                     setTextoBusca(e.target.value);
                     if (!e.target.value.trim() && filtros.busca) executarBusca('');
                   }}
                   onKeyDown={e => { if (e.key === 'Enter') executarBusca(); }}
                   style={{
                     width: '320px', height: '28px', padding: '0 32px 0 10px',
                     border: `1px solid ${filtros.busca ? COR.azul : COR.borda}`,
                     borderRadius: '4px',
                     fontSize: '12px', fontFamily: "'Manrope', sans-serif",
                     boxSizing: 'border-box',
                   }} />
            <button type="button" onClick={() => executarBusca()} title="Buscar"
                    style={{
                      position: 'absolute', right: '2px', top: '2px',
                      width: '26px', height: '24px', padding: 0,
                      border: 'none', borderRadius: '3px',
                      background: 'transparent', cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}>
              <IconeBusca tamanho={14} cor={filtros.busca ? COR.azul : COR.muted} />
            </button>
          </div>

          {/* r220: Ordenação — dropdown próprio, com ícone SVG por opção.
              <option> nativo só aceita texto, por isso as opções eram emoji. */}
          <SelectOrdemTarefas
            valor={filtros.ordem}
            onChange={v => atualizarFiltro('ordem', v)}
          />

          {/* Botão Filtros (expandir/recolher) */}
          <button
            onClick={() => setFiltrosExpandidos(e => !e)}
            style={{
              height: '28px', padding: '0 10px',
              border: `1px solid ${totalFiltrosAtivos > 0 ? COR.azul : COR.borda}`,
              borderRadius: '4px',
              backgroundColor: filtrosExpandidos
                ? COR.azulBg
                : (totalFiltrosAtivos > 0 ? COR.azulBg : COR.branco),
              color: totalFiltrosAtivos > 0 ? COR.azul : COR.texto,
              fontSize: '11px', fontWeight: '600',
              cursor: 'pointer',
              fontFamily: "'Manrope', sans-serif",
              display: 'flex', alignItems: 'center', gap: '6px',
            }}
            title="Mostrar/ocultar filtros avançados"
          >
            <span>⚙ Filtros</span>
            {totalFiltrosAtivos > 0 && (
              <span style={{
                backgroundColor: COR.azul, color: COR.branco,
                fontSize: '9px', fontWeight: '700',
                padding: '1px 5px', borderRadius: '8px',
              }}>{totalFiltrosAtivos}</span>
            )}
            <span style={{ fontSize: '8px' }}>{filtrosExpandidos ? '▴' : '▾'}</span>
          </button>

          {totalFiltrosAtivos > 0 && (
            <button
              onClick={limparFiltros}
              title="Limpar todos os filtros"
              style={{
                height: '28px', padding: '0 8px',
                border: `1px solid ${COR.borda}`, borderRadius: '4px',
                backgroundColor: COR.branco, color: COR.muted,
                fontSize: '11px', fontWeight: '600',
                cursor: 'pointer',
                fontFamily: "'Manrope', sans-serif",
              }}
            >× Limpar</button>
          )}

          <SePode rotina="tarefas" acao="incluir">
            <BtnIcone icone="incluir" onClick={abrirNova} title="Nova tarefa" cor={COR.azul} solido />
          </SePode>
        </div>

        {/* ─── LINHA 2: Filtros avançados (recolhível) ─────────────────── */}
        {filtrosExpandidos && (
          <div style={{
            padding: '12px 14px',
            display: 'flex', alignItems: 'center', gap: '8px',
            flexWrap: 'wrap',
            backgroundColor: COR.cinzaBg,
          }}>
            {/* Multi-selects checkbox (Status foi pra linha 1 — r221) */}
            <MultiSelect
              compacto
              label="Setor"
              valores={filtros.setor}
              onChange={v => atualizarFiltro('setor', v)}
              opcoes={Object.entries(SETOR).map(([v, info]) => ({
                v, label: info.label, cor: info.cor,
              }))}
              largura="130px"
            />
            <MultiSelect
              compacto
              label="Prioridade"
              valores={filtros.prioridade}
              onChange={v => atualizarFiltro('prioridade', v)}
              opcoes={Object.entries(PRIORIDADE).map(([v, info]) => ({
                v, label: info.label, cor: info.cor,
              }))}
              largura="130px"
            />

            {/* Multi-selects de IDs (extraídos das tarefas em memória) */}
            <MultiSelect
              compacto
              label="Cliente"
              valores={filtros.cliente_ids}
              onChange={v => atualizarFiltro('cliente_ids', v)}
              opcoes={opcoesClientes}
              largura="160px"
            />
            <MultiSelect
              compacto
              label="Projeto"
              valores={filtros.negocio_ids}
              onChange={v => atualizarFiltro('negocio_ids', v)}
              opcoes={opcoesProjetos}
              largura="160px"
            />
            <MultiSelect
              compacto
              label="Solicitante"
              valores={filtros.solicitante_ids}
              onChange={v => atualizarFiltro('solicitante_ids', v)}
              opcoes={opcoesSolicitantes}
              largura="140px"
            />
            <MultiSelect
              compacto
              label="Responsável"
              valores={filtros.responsavel_ids}
              onChange={v => atualizarFiltro('responsavel_ids', v)}
              opcoes={opcoesResponsaveis}
              largura="140px"
            />

            {/* Datas */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '0 6px', borderLeft: `1px solid ${COR.borda}`,
            }}>
              <span style={{ fontSize: '10px', color: COR.muted, fontWeight: '600' }}>
                Programada:
              </span>
              <input type="date" value={filtros.data_prog_de}
                     onChange={e => atualizarFiltro('data_prog_de', e.target.value)}
                     style={{
                       height: '28px', padding: '0 6px',
                       border: `1px solid ${COR.borda}`, borderRadius: '4px',
                       fontSize: '11px', fontFamily: "'Manrope', sans-serif",
                     }} />
              <span style={{ fontSize: '10px', color: COR.muted }}>até</span>
              <input type="date" value={filtros.data_prog_ate}
                     onChange={e => atualizarFiltro('data_prog_ate', e.target.value)}
                     style={{
                       height: '28px', padding: '0 6px',
                       border: `1px solid ${COR.borda}`, borderRadius: '4px',
                       fontSize: '11px', fontFamily: "'Manrope', sans-serif",
                     }} />
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '0 6px', borderLeft: `1px solid ${COR.borda}`,
            }}>
              <span style={{ fontSize: '10px', color: COR.muted, fontWeight: '600' }}>
                Deadline:
              </span>
              <input type="date" value={filtros.deadline_de}
                     onChange={e => atualizarFiltro('deadline_de', e.target.value)}
                     style={{
                       height: '28px', padding: '0 6px',
                       border: `1px solid ${COR.borda}`, borderRadius: '4px',
                       fontSize: '11px', fontFamily: "'Manrope', sans-serif",
                     }} />
              <span style={{ fontSize: '10px', color: COR.muted }}>até</span>
              <input type="date" value={filtros.deadline_ate}
                     onChange={e => atualizarFiltro('deadline_ate', e.target.value)}
                     style={{
                       height: '28px', padding: '0 6px',
                       border: `1px solid ${COR.borda}`, borderRadius: '4px',
                       fontSize: '11px', fontFamily: "'Manrope', sans-serif",
                     }} />
            </div>
          </div>
        )}
      </div>

      {/* Lista de cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {carregando ? (
          <div style={{ textAlign: 'center', padding: '40px', color: COR.muted }}>
            Carregando tarefas...
          </div>
        ) : listaFiltrada.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '40px', color: COR.muted,
            backgroundColor: COR.branco, border: `1px dashed ${COR.borda}`,
            borderRadius: '8px',
          }}>
            Nenhuma tarefa encontrada com os filtros atuais.
          </div>
        ) : (
          listaFiltrada.map(t => (
            <CardTarefa
              key={t.id}
              tarefa={t}
              onAbrir={abrirTarefaDoCard}
              onEditar={editarTarefa}
              onExcluir={excluirTarefa}
              onAtualizarCampos={atualizarCamposTarefa}
              onAlterarStatus={alterarStatusTarefa}
              podeAlterar={pode('tarefas', 'alterar')}
              ehAdmin={isAdmin || isBootstrap}
              usuarioLogadoId={usuario?.id}
            />
          ))
        )}
      </div>

      {/* Modal de criação/visualização/edição */}
      {painel && (
        <TarefaModal
          aberto={!!painel}
          modo={painel}
          tarefa={selecionada}
          onFechar={fecharPainel}
          onSalvar={aoSalvar}
          onAtualizar={carregar}
          onTrocarModoEditar={() => setPainel('editar')}
        />
      )}

      {/* r186: alerta de notificações não lidas (fica ACIMA do TarefaModal) */}
      <PopupNotificacoesTarefas alerta={alerta} onAbrirTarefa={abrirTarefaPorId} />
    </div>
  );
}

export default TarefasPageLista;
